import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, MapPin } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // Show session expired message if redirected due to token expiration
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.info('Your session has expired. Please sign in again.', { duration: 5000 });
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${user.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-[hsl(243,65%,20%)]/90" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-7 h-7" />
            </div>
            <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] tracking-tight mb-6">
            Time tracking,<br />simplified.
          </h1>
          <p className="text-lg text-white/80 max-w-md leading-relaxed">
            Geofence-enabled clock-in system for modern workplaces. Track attendance, manage breaks, and generate reports effortlessly.
          </p>
          <div className="flex items-center gap-6 mt-12">
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-5 h-5" />
              <span>Geofence Verified</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Clock className="w-5 h-5" />
              <span>Real-time Tracking</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-xl font-bold font-['Manrope'] text-[hsl(243,65%,20%)]">ClockIn Pro</span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-['Manrope']">Welcome Back</CardTitle>
              <CardDescription>Sign in to your account to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <Link to="/forgot-password" className="text-sm text-[hsl(243,65%,50%)] hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? <div className="spinner" /> : 'Sign In'}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Employee? You'll receive an invite link from your admin to register.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
